import React from 'react';
import { useNavigate } from 'react-router';
import MagicButton from 'components/MagicButton/MagicButton';
import { Points } from 'components/Points/Points';
import { TradeEvent } from 'types/interfaces/followingFeed';
import { FeedCardShell, profileHref } from './FeedCardShell';
import { CardFan } from './CardFan';

interface TradeEventCardProps {
  event: TradeEvent;
}

const THUMB_LIMIT = 3;

const connectorFor = (
  event: TradeEvent,
): { connector: string; primaryLabel: string } => {
  const cardsWord = event.payload.cardCount === 1 ? 'a card' : 'cards';
  switch (event.type) {
    case 'TRADE_INITIATED':
      return {
        connector: 'started a trade with',
        primaryLabel: 'View trade',
      };
    case 'TRADE_SHIPPED':
      return {
        connector: 'shipped a trade to',
        primaryLabel: 'View trade',
      };
    case 'TRADE_COMPLETED':
    default:
      return {
        connector: `traded ${cardsWord} to`,
        primaryLabel: `View ${event.payload.receiver.username}`,
      };
  }
};

/** Whether the subline shows a status pill (INITIATED skips it). */
const shouldShowStatus = (type: TradeEvent['type']): boolean =>
  type === 'TRADE_SHIPPED' || type === 'TRADE_COMPLETED';

const statusLabel = (event: TradeEvent): string => {
  switch (event.type) {
    case 'TRADE_SHIPPED':
      return 'SHIPPED';
    case 'TRADE_COMPLETED':
      // Use backend-provided status if present; fall back to COMPLETED.
      return event.payload.status?.toUpperCase() || 'COMPLETED';
    default:
      return '';
  }
};

export const TradeEventCard: React.FC<TradeEventCardProps> = ({ event }) => {
  const { actor, payload } = event;
  const { receiver, cards, totalValue, cardCount } = payload;
  const navigate = useNavigate();

  const { connector, primaryLabel } = connectorFor(event);

  const hero =
    cards.length > 0 ? (
      <CardFan
        cards={cards}
        max={THUMB_LIMIT}
        showOverflowLabel
        totalCount={cardCount}
      />
    ) : null;

  const subline = (
    <div className="feed-trade-subline">
      <Points type="small" points={totalValue} />
      <span className="feed-trade-sep">·</span>
      <span>
        {cardCount} {cardCount === 1 ? 'card' : 'cards'}
      </span>
      {shouldShowStatus(event.type) && (
        <>
          <span className="feed-trade-sep">·</span>
          <span className="feed-trade-status">{statusLabel(event)}</span>
        </>
      )}
    </div>
  );

  const secondaryLabel =
    event.type === 'TRADE_COMPLETED'
      ? `View ${actor.username}`
      : 'View sender';

  // TRADE_SHIPPED is informational only — no CTA row. Header avatars/usernames
  // remain linked to their profiles for navigation.
  const footer =
    event.type === 'TRADE_SHIPPED' ? undefined : (
      <>
        <MagicButton
          label={primaryLabel}
          color="purple"
          onPress={() =>
            navigate(
              profileHref({
                username: receiver.username,
                slug: receiver.slug,
                avatarUrl: receiver.avatarUrl,
              }),
            )
          }
        />
        <MagicButton
          label={secondaryLabel}
          color="dark-grey"
          onPress={() => navigate(profileHref(actor))}
        />
      </>
    );

  return (
    <FeedCardShell
      actor={actor}
      secondaryActor={{
        username: receiver.username,
        slug: receiver.slug,
        avatarUrl: receiver.avatarUrl,
        isElite: receiver.isElite ?? false,
        showAnimatedBorder: receiver.showAnimatedBorder,
      }}
      actionText=""
      connectorText={connector}
      createdAt={event.createdAt}
      hero={hero}
      subline={subline}
      footer={footer}
    />
  );
};

export default TradeEventCard;
